import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  PasswordInput,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconAlertCircle,
  IconCalendar,
  IconCheck,
  IconKey,
  IconMail,
  IconShield,
  IconShieldCheck,
  IconUser,
} from "@/lib/icons";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { ApiError } from "@/lib/api/client";
import { changePassword, getMe, updateMe } from "@/lib/api/auth";
import { formatDateTime } from "@/lib/format";
import { useAuthStore } from "@/stores/auth";

function passwordStrength(value: string): number {
  if (!value) return 0;
  let score = 0;
  if (value.length >= 8) score += 25;
  if (value.length >= 12) score += 25;
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 25;
  if (/\d/.test(value) && /[^A-Za-z0-9]/.test(value)) score += 25;
  return Math.min(score, 100);
}

export function ProfilePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const storeUser = useAuthStore((s) => s.user);

  const meQuery = useQuery({
    queryKey: ["auth-me"],
    queryFn: getMe,
  });

  const user = meQuery.data?.user ?? storeUser;

  const nameForm = useForm({
    initialValues: { full_name: user?.full_name ?? "" },
    validate: {
      full_name: (v) => (v.trim().length > 0 ? null : t("validation.required")),
    },
  });

  const nameMutation = useMutation({
    mutationFn: (values: { full_name: string }) =>
      updateMe({ full_name: values.full_name.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth-me"] });
      notifications.show({ color: "green", message: t("profile.name_updated") });
    },
  });

  const passwordForm = useForm({
    initialValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
    validate: {
      current_password: (v) => (v.length > 0 ? null : t("validation.required")),
      new_password: (v) =>
        v.length >= 12 ? null : t("users.validation.password_min"),
      confirm_password: (v, values) =>
        v === values.new_password ? null : t("profile.password_mismatch"),
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (values: { current_password: string; new_password: string }) =>
      changePassword(values),
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: t("profile.password_changed"),
      });
      passwordForm.reset();
    },
  });

  const passwordError = (() => {
    if (!passwordMutation.error) return null;
    if (passwordMutation.error instanceof ApiError) {
      if (passwordMutation.error.code === "auth_failed") {
        return t("profile.password_current_wrong");
      }
      return passwordMutation.error.message;
    }
    return t("auth.network_error");
  })();

  if (!user) return null;

  const initials = user.full_name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const formatLocalDate = (iso: string | null | undefined) =>
    formatDateTime(iso);

  const newPasswordValue = passwordForm.values.new_password;
  const strength = passwordStrength(newPasswordValue);
  const strengthColor = strength >= 75 ? "brand" : strength >= 50 ? "orange" : "red";
  const strengthLabel =
    strength >= 75
      ? t("profile.strength.strong")
      : strength >= 50
        ? t("profile.strength.medium")
        : strength > 0
          ? t("profile.strength.weak")
          : "";

  return (
    <Stack gap="lg">
      <PageHeader title={t("profile.title")} subtitle={t("profile.subtitle")} back />

      <Card
        padding="xl"
        radius="xl"
        style={{
          background:
            "linear-gradient(135deg, var(--mantine-color-brand-7) 0%, var(--mantine-color-brand-9) 100%)",
          color: "white",
          overflow: "hidden",
        }}
      >
        <Group gap="lg" wrap="nowrap">
          <Avatar
            size={96}
            radius="xl"
            variant="white"
            color="brand"
            style={{ border: "4px solid rgba(255,255,255,0.25)" }}
          >
            <Text fw={800} size="1.6rem" c="brand.7">
              {initials || <IconUser size={40} strokeWidth={1.5} />}
            </Text>
          </Avatar>
          <Stack gap={6} style={{ flex: 1 }}>
            <Title order={2} fw={800} c="white" lh={1.1}>
              {user.full_name}
            </Title>
            <Group gap="xs">
              <Badge variant="white" color="brand" radius="sm" size="md">
                {t(`users.roles.${user.role}`)}
              </Badge>
              {user.specialty && (
                <Badge
                  variant="filled"
                  color="rgba(255,255,255,0.18)"
                  c="white"
                  radius="sm"
                  size="md"
                >
                  {t(`users.specialties.${user.specialty}`)}
                </Badge>
              )}
            </Group>
            <Group gap="md" mt={6}>
              <Group gap={6}>
                <IconMail size={14} strokeWidth={2} />
                <Text size="sm" style={{ opacity: 0.9 }}>
                  {user.email}
                </Text>
              </Group>
              <Group gap={6}>
                <IconCalendar size={14} strokeWidth={2} />
                <Text size="sm" style={{ opacity: 0.9 }}>
                  {formatLocalDate(user.last_login_at)}
                </Text>
              </Group>
            </Group>
          </Stack>
        </Group>
      </Card>

      <Card padding="xl" radius="xl">
        <Group gap="sm" mb="md">
          <ThemeIcon size={36} radius="xl" variant="light" color="brand">
            <IconUser size={18} strokeWidth={2} />
          </ThemeIcon>
          <Title order={5} fw={700}>
            {t("profile.edit_name")}
          </Title>
        </Group>
        <form onSubmit={nameForm.onSubmit((v) => nameMutation.mutate(v))}>
          <Group align="flex-end" gap="md">
            <TextInput
              label={t("users.fields.full_name")}
              required
              style={{ flex: 1 }}
              {...nameForm.getInputProps("full_name")}
            />
            <Button
              type="submit"
              loading={nameMutation.isPending}
              radius="xl"
              leftSection={<IconCheck size={16} strokeWidth={2.4} />}
            >
              {t("common.save")}
            </Button>
          </Group>
        </form>
      </Card>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        <Card padding="xl" radius="xl">
          <Group gap="sm" mb="lg">
            <ThemeIcon size={36} radius="xl" variant="light" color="brand">
              <IconKey size={18} strokeWidth={2} />
            </ThemeIcon>
            <Stack gap={0}>
              <Title order={5} fw={700}>
                {t("profile.change_password")}
              </Title>
              <Text size="xs" c="dimmed">
                {t("profile.password_hint")}
              </Text>
            </Stack>
          </Group>

          {passwordError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} radius="lg" mb="md">
              {passwordError}
            </Alert>
          )}

          <form
            onSubmit={passwordForm.onSubmit((values) =>
              passwordMutation.mutate({
                current_password: values.current_password,
                new_password: values.new_password,
              }),
            )}
          >
            <Stack gap="md">
              <PasswordInput
                label={t("profile.current_password")}
                placeholder="••••••••"
                required
                {...passwordForm.getInputProps("current_password")}
              />
              <PasswordInput
                label={t("profile.new_password")}
                placeholder="••••••••••••"
                description={t("users.fields.password_hint")}
                required
                {...passwordForm.getInputProps("new_password")}
              />
              {newPasswordValue.length > 0 && (
                <Box>
                  <Group justify="space-between" mb={6}>
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                      {t("profile.strength.label")}
                    </Text>
                    <Text size="xs" fw={700} c={`${strengthColor}.7`}>
                      {strengthLabel}
                    </Text>
                  </Group>
                  <Progress value={strength} color={strengthColor} size="sm" radius="xl" />
                </Box>
              )}
              <PasswordInput
                label={t("profile.confirm_password")}
                placeholder="••••••••••••"
                required
                {...passwordForm.getInputProps("confirm_password")}
              />
              <Group justify="flex-end" mt="xs">
                <Button
                  type="submit"
                  loading={passwordMutation.isPending}
                  radius="xl"
                  leftSection={<IconCheck size={16} strokeWidth={2.4} />}
                  size="md"
                >
                  {t("profile.update_password")}
                </Button>
              </Group>
            </Stack>
          </form>
        </Card>

        <Card padding="xl" radius="xl">
          <Group gap="sm" mb="lg">
            <ThemeIcon size={36} radius="xl" variant="light" color="brand">
              <IconUser size={18} strokeWidth={2} />
            </ThemeIcon>
            <Title order={5} fw={700}>
              {t("profile.account_info")}
            </Title>
          </Group>
          <Stack gap="sm">
            <InfoRow
              icon={<IconMail size={16} strokeWidth={2} />}
              label={t("users.fields.email")}
              value={user.email}
              mono
            />
            <Divider />
            <InfoRow
              icon={<IconShield size={16} strokeWidth={2} />}
              label={t("users.fields.role")}
              value={t(`users.roles.${user.role}`)}
            />
            {user.specialty && (
              <>
                <Divider />
                <InfoRow
                  icon={<IconShieldCheck size={16} strokeWidth={2} />}
                  label={t("users.fields.specialty")}
                  value={t(`users.specialties.${user.specialty}`)}
                />
              </>
            )}
            <Divider />
            <InfoRow
              icon={<IconCalendar size={16} strokeWidth={2} />}
              label={t("profile.last_login")}
              value={formatLocalDate(user.last_login_at)}
            />
          </Stack>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}

function InfoRow({
  icon,
  label,
  value,
  mono,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Group justify="space-between" align="center" wrap="nowrap" gap="xs" py={4}>
      <Group gap={10} wrap="nowrap" style={{ flexShrink: 0 }}>
        <Box c="brand.7">{icon}</Box>
        <Text c="dimmed" size="sm" fw={500}>
          {label}
        </Text>
      </Group>
      <Text size="sm" ff={mono ? "monospace" : undefined} ta="right" fw={600}>
        {value}
      </Text>
    </Group>
  );
}
